import { Skeleton, TableCell, TableRow, Tooltip } from "@mui/material"
import { useLocation, useNavigate } from "react-router-dom"

import { RootPath as RootPathDesktop } from "@/App/Equities/desktop/paths"
import { RootPath as RootPathMobile } from "@/App/Equities/mobile/paths"
import {
  ChangeAmount,
  ChangePercentage,
  CurrentPriceValue,
  InstrumentLogo,
} from "@/App/Equities/shared/components"
import { Typography } from "@/generated/material"
import type { Instrument } from "@/generated/TradingGateway"
import { useIsDesktop } from "@/utils/useIsDesktop"
import { withSubscribe } from "@/utils/withSubscribe"

interface InstrumentRowProps {
  row: Instrument
  testId: string
}

export const InstrumentRow = withSubscribe(
  ({ row, testId }: InstrumentRowProps) => {
    const navigate = useNavigate()
    const isDesktop = useIsDesktop()

    const rootPath = isDesktop ? RootPathDesktop : RootPathMobile
    const isSelected =
      useLocation().pathname === rootPath.tradeInstrument(row.symbol)

    return (
      <TableRow
        key={row.name}
        data-testid={testId}
        hover
        selected={isSelected}
        onClick={() => navigate(rootPath.tradeInstrument(row.symbol))}
        sx={({ palette }) => ({
          "&:active": {
            backgroundColor: palette["bg-secondary_subtle"],
          },
        })}
      >
        <TableCell>
          <InstrumentLogo
            variant="sm"
            symbol={row.symbol}
            data-testid={`logo`}
          />
        </TableCell>
        <TableCell align="left" data-testid={`${testId}.name`}>
          <Tooltip title={row.displayName || row.name}>
            <Typography
              color="text-primary"
              variant={"Text md/Regular"}
              noWrap
              maxWidth={140}
              display="block"
            >
              {row.displayName || row.name}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell align="right" data-testid={`${testId}.lastPrice`}>
          <Typography variant={"Text md/Regular"}>
            <CurrentPriceValue symbol={row.symbol} />
          </Typography>
        </TableCell>
        <TableCell align="right" data-testid={`${testId}.priceChange`}>
          <ChangeAmount symbol={row.symbol} />
        </TableCell>
        <TableCell align="right" data-testid={`${testId}.lastDayChange`}>
          <ChangePercentage symbol={row.symbol} />
        </TableCell>
      </TableRow>
    )
  },
  {
    fallback: (
      <TableRow>
        <TableCell>
          <Skeleton width={50} />
        </TableCell>
      </TableRow>
    ),
  },
)
